import ComponentManager from "../../componentFramework";
import EditContentNode from "../EditContentNode";
import JsfJsCommand from "../JsfJsCommand";
import {NativeJsfAjaxEvent, NativeJsfAjaxEventStatus, NativeJsfAjaxRequestOptions} from "../../scripts/jsf/JsfAjax";
import Queue from "../Queue";
import EventListenerManager from "../EventListenerManager";
import JsfAjaxScrollImprovements from "../../scripts/jsf/JsfAjaxScrollImprovements";

@ComponentManager.register()
export default class EditContent {
	public static componentStyleClass = "editContent";

	public readonly queue = new Queue()
	private readonly nodesQueuedToSave = new Set<EditContentNode>();
	private readonly eventListenerManager = new EventListenerManager();
	private jsfAjaxScrollImprovements: JsfAjaxScrollImprovements | undefined;

	private promise: Promise<any> | null = null;

	public static getInstance(element: Element | null, selfOrAncestor: boolean = true): EditContent | undefined {
		if (element === null) {
			return undefined;
		}
		const instanceElement = selfOrAncestor ?
			element.closest<HTMLElement>("." + EditContent.componentStyleClass)
			: element.querySelector<HTMLElement>("." + EditContent.componentStyleClass);
		return instanceElement === null ? undefined : ComponentManager.getComponent(instanceElement, EditContent);
	}

	constructor(public readonly rootElement: HTMLElement) {
		/* TODO - Need to tie the following into this saving code. These actions need to trigger a save and possibly wait for queue to be finished:
		            Closing browser window/tab, switching away from tab, buttons in the lower right corner, and switching editor tabs, etc.
		 		 Watch for: beforeunload and visibilitychange and pagehide - Trigger the same as "change" with delay of 0. ALSO, tell it to save ALL.. When the event is
		 		    closing or leaving the page we need to stop that until the save is complete.
		 */
		this.queue.registerType<boolean>({typeName: "autosave", priority: 1000, delay: 3000,
			merge: (existing, current) => current,
			action: data => this.doSave(data)
		});
		this.queue.registerType<string>({typeName: "editorAction", priority: 0, triggers: ["autosave"],
			merge: (existing, current) => current});
	}

	@ComponentManager.on("ready")
	private componentReady() {
		// Enhance the scrolling of the sidebar area
		const editContentNodesElement = this.rootElement.querySelector(".editContentNodes");
		if (editContentNodesElement !== null) {
			const updateIds = [editContentNodesElement.id];

			this.jsfAjaxScrollImprovements = new JsfAjaxScrollImprovements(
				this.rootElement.ownerDocument,
				{
					getScrollArea: (): Element | null => {
						return this.rootElement.querySelector(".editContent__nodes");
					},
					onUpdateIds: updateIds,
					selectedItemQuery: ".editContentNode--focused:not(.editContentNode--notValueHolder), .editContentGroup--containsFocus",
					preferCenteringSelectedIfSourceNotAncestor: true,
					alwaysRememberScrollPosition: true
				}
			);
		}
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.jsfAjaxScrollImprovements?.destroy();
		this.queue.cancel();
		this.eventListenerManager.removeListeners();
	}

	public addToSave(editContentNode: EditContentNode) {
		this.nodesQueuedToSave.add(editContentNode);

		this.startAutoSaveTimeout();

		this.updateSaveStatusUi();
	}

	public removeFromSave(editContentNode: EditContentNode) {
		this.nodesQueuedToSave.delete(editContentNode);
	}

	private startAutoSaveTimeout() {
		this.queue.enqueue("autosave", false);
	}

	public save(executeEntireForm: boolean = false) {
		this.queue.enqueue("autosave", executeEntireForm, undefined, 0);
	}

	private doSave(executeEntireForm: boolean): Promise<any> | null {
		if (!executeEntireForm && this.nodesQueuedToSave.size === 0) {
			return this.promise;
		}

		let prom = new Promise<boolean>((resolve, reject) => {
			// Copy list of nodes to save, and check for any currently being saved. If any are currently saving, move back to queue.
			const nodesSaving = executeEntireForm
				? ComponentManager.queryForAllComponents(this.rootElement, EditContentNode)
				: Array.from(this.nodesQueuedToSave);
			this.nodesQueuedToSave.clear();
			nodesSaving.forEach(node => {
				if (node.isSaving()) {
					this.nodesQueuedToSave.add(node);
				}
			});
			if (this.nodesQueuedToSave.size > 0) {
				this.startAutoSaveTimeout();
			}

			// Build values for "execute" and "render" to use for the JSF AJAX event
			const executes = new Array<string>();
			const renders = new Array<string>();
			nodesSaving.forEach(node => {
				executes.push(...node.getExecutesForJsfAjax());
				renders.push(...node.getRendersForJsfAjax());
			});

			// Prepare onevent and onerror
			const onevent = (ev: NativeJsfAjaxEvent) => {
				// "begin" event
				// Tell each node they are being saved
				if (ev.status === NativeJsfAjaxEventStatus.Begin) {
					nodesSaving.forEach(node => {
						node.onSaving();
					});
				} else {
					// "success" event
					// Tell each node they have been saved
					if (ev.status === NativeJsfAjaxEventStatus.Success) {
						nodesSaving.forEach(node => {
							node.onSaved();
						});
						resolve(true);
					}
				}
			}
			const onerror = (ev: NativeJsfAjaxEvent) => {
				// Tell each node there was an error
				nodesSaving.forEach(node => {
					node.onErrorDuringSave();
				});
				reject(ev.description ?? ev.responseText ?? "error");
			}

			// Run jsf autosaveAll
			const options: NativeJsfAjaxRequestOptions = {
				execute: !executeEntireForm && executes.length > 0 ? executes.join(" ") : "@form",
				render: renders.length > 0 ? renders.join(" ") : "@none",
				onevent: onevent,
				onerror: onerror,
				params: {"autosaving": "true"}
			};
			JsfJsCommand.run("autosaveAll", this.rootElement, options);

			// this.updateSaveStatusUi(); // TODO - Put in correct places: (1) Saving started; (2) Saving ended
		});

		const clear = (res: boolean) => {
			if (this.promise === prom) {
				this.promise = null;
			}
			return res;
		};

		if (this.promise !== null) {
			const prm = prom;
			const promiseReturner = () => prm;
			prom = this.promise.then(promiseReturner, promiseReturner);
		}

		this.promise = prom;
		return prom.then(clear, clear);
	}

	private updateSaveStatusUi() {
		// TODO - Visual show status - Create a method to update displayed status: tab and text display
	}
}

